import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Database,
  FileText,
  Play,
  Search,
  Server,
  XCircle,
} from 'lucide-react';
import { fmt, fmtDate, fmtRelative, statusBadgeClass } from './formatters';
import {
  PIPELINE_ACTIVE,
  PIPELINE_STEP_ICONS,
  pipelineNodeStatusClass,
} from './pipelineConstants';
import { summarizeSchedule } from './scheduleHelpers';

function StatusPill({ status, onClick, isClickable, isExpanded }) {
  if (!status || status === 'idle') {
    return <span className="ahri-dash-badge ahri-dash-badge--secondary">Not run</span>;
  }
  if (isClickable) {
    return (
      <button
        type="button"
        className={`ahri-dash-badge ahri-dash-badge--button ${statusBadgeClass(status)
          .replace('ahri-dash-badge ', '')}`}
        onClick={onClick}
        aria-expanded={isExpanded}
        aria-label={`${status} — click to ${isExpanded ? 'hide' : 'view'} error details`}
        title="Click to view the failure reason"
      >
        {status}
        {isExpanded ? (
          <ChevronUp size={11} className="ms-1" aria-hidden />
        ) : (
          <ChevronDown size={11} className="ms-1" aria-hidden />
        )}
      </button>
    );
  }
  return <span className={statusBadgeClass(status)}>{status}</span>;
}

function ErrorDetailsPanel({ step }) {
  const message = step.last_error || step.error_log;
  if (!message) {
    return (
      <div className="ahri-dash-pipeline__error-panel alert alert-warning d-flex align-items-start gap-2 py-2 mt-2 mb-0">
        <AlertTriangle size={16} className="flex-shrink-0 mt-1" aria-hidden />
        <div className="small">
          <div className="fw-semibold">This step is in a failed state</div>
          <div className="text-muted">
            The last import job ended in failure but no error details were
            recorded. Check the job page for more context.
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="ahri-dash-pipeline__error-panel alert alert-danger py-2 mt-2 mb-0">
      <div className="d-flex align-items-start gap-2">
        <AlertTriangle size={16} className="flex-shrink-0 mt-1" aria-hidden />
        <div className="small flex-grow-1">
          <div className="fw-semibold mb-1">Last import error</div>
          {step.last_job_file_name ? (
            <div className="text-muted mb-1">
              File: <code>{step.last_job_file_name}</code>
            </div>
          ) : null}
          <pre
            className="mb-0"
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
              fontSize: '0.85em',
            }}
          >
            {message}
          </pre>
          {step.last_job_id ? (
            <div className="text-muted mt-1">
              Job ID: <code>{step.last_job_id}</code>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LastCheckLine({ lastCheck, nowMs }) {
  if (!lastCheck || !lastCheck.at) return null;
  const status = String(lastCheck.status || '').toLowerCase();
  const isNoFile = status === 'no_file';
  const isFailed = status === 'failed';
  let Icon = Search;
  let toneClass = 'text-muted';
  let label = 'Last checked';
  if (isNoFile) {
    Icon = Search;
    toneClass = 'text-muted';
    label = 'Last checked · no file found';
  } else if (isFailed) {
    Icon = XCircle;
    toneClass = 'text-danger';
    label = 'Last checked · failed';
  } else if (status === 'imported') {
    Icon = CheckCircle2;
    toneClass = 'text-success';
    label = 'Last checked · imported';
  }
  return (
    <div className={`ahri-dash-pipeline__lastcheck small mt-2 pt-2 ${toneClass}`}>
      <div className="d-flex align-items-center gap-1 fw-medium">
        <Icon size={12} aria-hidden />
        <span>{label}</span>
      </div>
      <div>{fmtDate(lastCheck.at)}</div>
      <div className="text-muted">{fmtRelative(lastCheck.at, nowMs)}</div>
      {lastCheck.message ? (
        <div className="text-muted text-truncate" title={lastCheck.message}>
          {lastCheck.message}
        </div>
      ) : null}
    </div>
  );
}

function ImportSummaryLine({ lastImport, lastFile, nowMs }) {
  if (!lastImport && !lastFile) return null;
  const newCount = lastImport?.new;
  const updatedCount = lastImport?.updated;
  const deletedCount = lastImport?.deleted;
  const deletedSupported = Boolean(lastImport?.deleted_supported);
  const fileName = lastImport?.file_name || lastFile?.name;
  const importedAt = lastFile?.at || lastImport?.finished_at;
  return (
    <div className="ahri-dash-pipeline__lastimport small mt-2 pt-2">
      <div className="d-flex align-items-center gap-1 fw-medium">
        <FileText size={12} aria-hidden />
        <span>Last imported file</span>
      </div>
      {fileName ? (
        <div className="text-truncate" title={fileName}>
          <code>{fileName}</code>
        </div>
      ) : (
        <div className="text-muted">—</div>
      )}
      {importedAt ? (
        <div className="text-muted">
          {fmtDate(importedAt)} · {fmtRelative(importedAt, nowMs)}
        </div>
      ) : null}
      {lastImport ? (
        <div className="d-flex flex-wrap gap-2 mt-1">
          <span className="ahri-dash-badge ahri-dash-badge--success">
            New {fmt(newCount)}
          </span>
          <span className="ahri-dash-badge ahri-dash-badge--info">
            Updated {fmt(updatedCount)}
          </span>
          <span
            className={`ahri-dash-badge ${
              deletedSupported ? 'ahri-dash-badge--warning' : 'ahri-dash-badge--secondary'
            }`}
            title={
              deletedSupported
                ? 'Rows removed in this run'
                : 'Documents v4 imports are upsert-only — delete count not applicable'
            }
          >
            Deleted {deletedSupported ? fmt(deletedCount) : 'n/a'}
          </span>
          {lastImport.errors_count ? (
            <span className="ahri-dash-badge ahri-dash-badge--danger">
              Errors {fmt(lastImport.errors_count)}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SyncBlock({ step, displayTick }) {
  const nowMs = Date.now();
  void displayTick;
  const lastSynced = step.finished_at || step.last_activity_at || step.started_at;
  const started = step.started_at;
  const schedule = step.schedule;
  const lastCheck = step.last_check || schedule?.last_check;
  const lastFile = step.last_file || schedule?.last_file;
  const lastImport = step.last_import;

  return (
    <div className="ahri-dash-pipeline__sync col-12 col-md-4 col-lg-3">
      <div className="ahri-dash-pipeline__sync-label text-muted">Last synced</div>
      {lastSynced ? (
        <>
          <div className="ahri-dash-pipeline__sync-time">{fmtDate(lastSynced)}</div>
          <div className="ahri-dash-pipeline__sync-relative text-muted">
            {fmtRelative(lastSynced, nowMs)}
          </div>
          {started && started !== lastSynced ? (
            <div className="ahri-dash-pipeline__sync-started small text-muted mt-1">
              Started {fmtDate(started)}
            </div>
          ) : null}
        </>
      ) : (
        <div className="ahri-dash-pipeline__sync-time text-muted">Never run</div>
      )}

      <ImportSummaryLine
        lastImport={lastImport}
        lastFile={lastFile}
        nowMs={nowMs}
      />
      <LastCheckLine lastCheck={lastCheck} nowMs={nowMs} />

      {step.schedulable ? (
        <div className="ahri-dash-pipeline__next mt-2 pt-2">
          <div className="ahri-dash-pipeline__sync-label text-muted">Next scheduled</div>
          {schedule && schedule.enabled && schedule.next_run_at ? (
            <>
              <div className="ahri-dash-pipeline__sync-time">{fmtDate(schedule.next_run_at)}</div>
              <div className="ahri-dash-pipeline__sync-relative text-muted">
                {fmtRelative(schedule.next_run_at, nowMs)}
              </div>
              <div className="ahri-dash-pipeline__sched-summary small text-muted mt-1">
                {summarizeSchedule(schedule)}
              </div>
            </>
          ) : schedule && !schedule.enabled ? (
            <div className="ahri-dash-pipeline__sync-time text-muted">Paused</div>
          ) : (
            <div className="ahri-dash-pipeline__sync-time text-muted">Not scheduled</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function PipelineStepRow({
  step,
  isLast,
  startingId,
  onRequestStart,
  onRequestSchedule,
  displayTick = 0,
}) {
  const Icon = PIPELINE_STEP_ICONS[step.id] || Database;
  const isActive = PIPELINE_ACTIVE.has(String(step.status || '').toLowerCase());
  const progress = step.progress || {};
  const showProgress = isActive && (progress.total != null || progress.current > 0);
  const nodeClass = pipelineNodeStatusClass(step.status);
  const hasSchedule = Boolean(step.schedule);
  const scheduleEnabled = hasSchedule && step.schedule?.enabled;
  const statusLower = String(step.status || '').toLowerCase();
  const isFailed = statusLower === 'failed';
  const hasErrorDetails = isFailed && Boolean(step.last_error || step.error_log);
  const [errorOpen, setErrorOpen] = useState(false);

  return (
    <li
      className={`ahri-dash-pipeline__step${isActive ? ' ahri-dash-pipeline__step--active' : ''}`}
    >
      <div className="ahri-dash-pipeline__rail" aria-hidden={isLast}>
        <span className={`ahri-dash-pipeline__node ${nodeClass}${isActive ? ' ahri-dash-pipeline__node--pulse' : ''}`}>
          <Icon size={20} strokeWidth={2} />
        </span>
        {!isLast ? <span className={`ahri-dash-pipeline__connector ${nodeClass}`} /> : null}
      </div>

      <div className="ahri-dash-pipeline__card flex-grow-1">
        <div className="row g-3 align-items-start">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <span className="ahri-dash-pipeline__order text-muted">Step {step.order}</span>
              <h6 className="mb-0 fw-semibold">{step.label}</h6>
              <StatusPill
                status={step.status}
                isClickable={isFailed}
                isExpanded={errorOpen}
                onClick={() => setErrorOpen((prev) => !prev)}
              />
              {hasSchedule ? (
                <span
                  className={`ahri-dash-badge ${scheduleEnabled ? 'ahri-dash-badge--info' : 'ahri-dash-badge--secondary'}`}
                  title={scheduleEnabled ? 'Schedule active' : 'Schedule paused'}
                >
                  <CalendarClock size={11} className="me-1" />
                  {scheduleEnabled ? 'Scheduled' : 'Paused'}
                </span>
              ) : null}
            </div>
            {step.source ? (
              <div className="ahri-dash-pipeline__source mb-1">{step.source}</div>
            ) : null}
            {step.data_source ? (
              <div className="ahri-dash-pipeline__remote small mb-1" title="Remote data source configured">
                <Server size={11} className="me-1 opacity-75" />
                <code>{step.data_source.summary}</code>
              </div>
            ) : null}
            {step.mapping_summary?.label ? (
              <div className="ahri-dash-pipeline__remote small mb-1" title="Saved field mapping">
                <FileText size={11} className="me-1 opacity-75" />
                Mapping: <code>{step.mapping_summary.label}</code>
              </div>
            ) : null}
            <p className="small text-muted mb-2 mb-md-0">{step.description}</p>
            {isActive && (progress.message || progress.detail) ? (
              <p className="small text-primary fw-medium mb-1 mt-2">
                {progress.message || progress.detail}
              </p>
            ) : (progress.detail || step.engine) ? (
              <div className="d-flex flex-wrap gap-3 small mt-2">
                {progress.detail ? (
                  <span>
                    <span className="text-muted">Detail: </span>
                    <span className="fw-medium">{progress.detail}</span>
                  </span>
                ) : null}
                {step.engine ? (
                  <span>
                    <span className="text-muted">Engine: </span>
                    <span className="fw-medium text-capitalize">{step.engine}</span>
                  </span>
                ) : null}
              </div>
            ) : null}
            {showProgress ? (
              <div className="ahri-dash-pipeline__progress mt-2">
                <div className="d-flex justify-content-between small mb-1">
                  <span className="text-primary fw-medium">
                    {progress.step ? `In progress · ${progress.step}` : 'In progress'}
                  </span>
                  <span>
                    {progress.total === 100
                      ? `${fmt(progress.current)}%`
                      : (
                        <>
                          {fmt(progress.current)}
                          {progress.total != null ? ` / ${fmt(progress.total)}` : ''}
                        </>
                      )}
                  </span>
                </div>
                {progress.total != null && progress.total > 0 ? (
                  <div className="progress ahri-dash-pipeline__progress-bar" style={{ height: '6px' }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{
                        width: `${Math.min(100, (Number(progress.current) / Number(progress.total)) * 100)}%`,
                      }}
                      aria-valuenow={progress.current}
                      aria-valuemin={0}
                      aria-valuemax={progress.total}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {step.start_hint && !step.can_start ? (
              <p className="small ahri-dash-pipeline__hint mb-0 mt-2">{step.start_hint}</p>
            ) : null}
            {isFailed && errorOpen ? <ErrorDetailsPanel step={step} /> : null}
            {isFailed && !errorOpen && hasErrorDetails ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mt-2 text-danger d-inline-flex align-items-center gap-1"
                onClick={() => setErrorOpen(true)}
              >
                <AlertTriangle size={12} aria-hidden />
                Show failure details
              </button>
            ) : null}
          </div>

          <SyncBlock step={step} displayTick={displayTick} />

          <div className="col-12 col-md-12 col-lg-3 d-flex flex-wrap gap-2 justify-content-lg-end align-items-start">
            {step.schedulable ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                onClick={() => onRequestSchedule?.(step)}
                aria-label={`Schedule ${step.label}`}
              >
                <CalendarClock size={14} />
                {hasSchedule ? 'Edit schedule' : 'Schedule'}
              </button>
            ) : null}
            {step.manage_path ? (
              <Link
                to={step.manage_path}
                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
              >
                Manage
                <ArrowUpRight size={14} />
              </Link>
            ) : null}
            <button
              type="button"
              className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
              disabled={!step.can_start || startingId === step.id || isActive}
              onClick={() => onRequestStart(step)}
            >
              <Play size={14} />
              {startingId === step.id ? 'Starting…' : 'Start'}
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

export default PipelineStepRow;
